import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';

import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import { IMAGE_PATH } from 'common/ImageConstants/ImagePathUtils';
import {
  isPickupOrderCancelled,
  isPickupOrderCheckedIn,
  isPickupOrderCompleted,
  isPickupOrderSentPOSCompleted,
  isCheckedInClosedOrder,
  isCheckedInAbandoned,
} from 'common/utilities/orderStatusUtils';

import { setEnablePickupCallSupportModal } from 'organisms/orderSummaryViews/utils/orderSummaryModelViewUtils';

import { PICKUP_COLLECTION_METHOD, SHARED_CONSTANTS, PAYMENT_TENDER_TYPES } from 'common/constants/SharedConstants';
import { ButtonComp, ButtonType, ContainedButton } from 'atoms/Buttons';
import { ChannelManager } from 'common/manager';
import { getDistanceByLatLong } from 'molecules/GoogleMap/GoogleMap';
import {
  getStatusMessageFromContentful,
  getSubStatusMessageFromContentful,
  checkPaymentTextForRefund,
} from 'common/utilities/orderDetailsUtils';

/*********************************************************************
 *
 *
 *********************************************************************/
const getPickupOrderStoreNameOrderIdPanelJSX = params => {
  const { orderDetails } = params;
  const { storeName, displayOrderId } = orderDetails;
  return (
    <>
      <div className={`pickup-store-order`}>{storeName}</div>
      <div className='pickup-store-order red'>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDER_HASH)?.toUpperCase() + displayOrderId}
      </div>
    </>
  );
};

/*********************************************************************
 *
 *
 *********************************************************************/
const getPickupOrderTimePanelJSX = params => {
  const { orderDetails } = params;
  const {
    orderStatus,
    deliveryOrEstimatedOrCollectAtTime,
    enableRetryCountForPickupOrderTracking,
    pickupOrderEstimatedOrCollectAtTime,
  } = orderDetails;
  const orderCollectedTime = enableRetryCountForPickupOrderTracking
    ? pickupOrderEstimatedOrCollectAtTime
    : deliveryOrEstimatedOrCollectAtTime;
  return !isCheckedInAbandoned(orderDetails) && (
    <>
      <div className={`estimated-arrival estimated-arrival-refund ${orderStatus}-delivery`}>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.COLLECTED_AT)}
      </div>
      <div className='idAndTime time-delivery'>{orderCollectedTime}</div>
    </>
  );
};

/*********************************************************************
 *
 *
 *********************************************************************/
const getPickupStatusIconJSX = params => {
  const { orderDetails } = params;
  const { orderStatus, orderSummarycontentData } = orderDetails;

  const imageSrcFn = () => {
    if (isPickupOrderSentPOSCompleted(orderDetails)) {
      return IMAGE_PATH?.TakeawayCompleted;
    } else if (isPickupOrderCancelled(orderDetails)) {
      return IMAGE_PATH?.CartError;
    } else if (isPickupOrderCheckedIn(orderDetails)) {
      return IMAGE_PATH?.ConfirmedImg;
    }
    return '';
  };
  const imageSrc = orderSummarycontentData?.icon?.fields?.file?.url ?? imageSrcFn();

  return imageSrc !== '' && <img src={imageSrc} className='statusIcon' alt={orderStatus} />;
};

/*********************************************************************
 *
 *
 *********************************************************************/
const getPickupOrderStatusMessageString = orderDetails => {
  if (isPickupOrderSentPOSCompleted(orderDetails)) {
    switch (orderDetails?.selectedPickupOrderCollectionMethod) {
      case PICKUP_COLLECTION_METHOD?.DRIVE_THRU:
        return translateWithI18Next(TRANSLATE_MAPPING_KEY?.CHECIN_AT_DRIVE_THRU);
      case PICKUP_COLLECTION_METHOD?.IN_STORE:
        return translateWithI18Next(TRANSLATE_MAPPING_KEY?.CHECIN_AT_IN_STORE);
      default:
        return `${translateWithI18Next(
          TRANSLATE_MAPPING_KEY?.ORDER_RECEIVED,
        )}, ${orderDetails?.customer?.firstName.replace(/(.{7})..+/, '$1...')}`;
    }
  } else if (isPickupOrderCancelled(orderDetails)) {
    return translateWithI18Next(TRANSLATE_MAPPING_KEY?.CANCEL_ORDER);
  } else if (isPickupOrderCheckedIn(orderDetails)) {
    return translateWithI18Next(TRANSLATE_MAPPING_KEY?.ENJOY_MEAL);
  }
  return '';
};

/*********************************************************************
 * triggerPickupCheckedIn
 * params
 *********************************************************************/
export const triggerPickupCheckedIn = params => {
  const { updateSetOrderDetails } = params;
  updateSetOrderDetails({
    isShowPickupOrderCheckReadyToPrepareModal: false,
    isPickupOrderUserConfirmedToCheckIn: true,
  });
};

/*********************************************************************
 * pickupOrderGetReadyToPrepareHandler
 * params
 *********************************************************************/
export const pickupOrderGetReadyToPrepareHandler = async params => {
  const pickupOrderGetReadyCheckDistance = 300;
  const { orderDetails, updateSetOrderDetails } = params;
  const { store } = orderDetails;
  await ChannelManager.BindLocation();

  updateSetOrderDetails({
    pickupClosedInfoModal: true,
  });
  if (isPickupOrderCheckedIn(orderDetails)) {
    updateSetOrderDetails({ isShowPickupOrderAlreadyCheckInModal: true });
  }
  if (ChannelManager.coordinates) {
    const { latitude, longitude } = store?.location || {};
    const diff = await getDistanceByLatLong({
      destinations: { latitude, longitude },
      origins: ChannelManager?.coordinates || {},
    });
    if (
      diff?.[0]?.status?.toLowerCase() === SHARED_CONSTANTS?.ZERO_RESULTS?.toLowerCase() ||
      diff?.[0]?.distance?.value > pickupOrderGetReadyCheckDistance
    ) {
      updateSetOrderDetails({
        isShowPickupOrderCheckReadyToPrepareModal: true,
      });
    } else {
      triggerPickupCheckedIn(params);
    }
  } else {
    triggerPickupCheckedIn(params);
  }
};

/*********************************************************************
 * getPickupOrderCheckedInMethodInfo
 * params
 *********************************************************************/
const getPickupOrderCheckedInMethodInfo = params => {
  const { orderDetails, isCheckinLoading } = params;
  const { selectedPickupOrderCollectionMethod } = orderDetails;
  const getButtonLabel = () => {
    switch (selectedPickupOrderCollectionMethod) {
      case PICKUP_COLLECTION_METHOD?.DRIVE_THRU:
        return translateWithI18Next(TRANSLATE_MAPPING_KEY?.I_AM_AT_DRIVE_THRU_QUEUE);
      case PICKUP_COLLECTION_METHOD?.IN_STORE:
        return translateWithI18Next(TRANSLATE_MAPPING_KEY?.I_AM_INSIDE_RESTAURANT);
      default:
        return '';
    }
  };
  return (
    isPickupOrderSentPOSCompleted(orderDetails) &&
    orderDetails?.selectedPickupOrderCollectionMethod && (
      <div className='pickup-collection-method-ready-to-prepare'>
        <div className='linebreak col-12'></div>
        <div>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.READY_TO_PREPARE_MEAL)}</div>
        <ContainedButton
          data-testid='pickup-collection-method-ready-to-prepare-btn'
          variant={ButtonType.primary}
          style={{ marginTop: '2rem' }}
          inactive={isCheckinLoading}
          loading={isCheckinLoading}
          onClick={() => pickupOrderGetReadyToPrepareHandler(params)}
          ariaLable={getButtonLabel()}
        >
          {getButtonLabel()}
        </ContainedButton>
      </div>
    )
  );
};

/*********************************************************************
 *
 *
 *********************************************************************/
export const getPickupStatusMessageJSX = params => {
  const { orderDetails } = params;
  const { orderStatus, service, selectedPickupOrderCollectionMethod, enableRetryCountForPickupOrderTracking } =
    orderDetails;
  const statusMessage = enableRetryCountForPickupOrderTracking
    ? getStatusMessageFromContentful(orderDetails)
    : getPickupOrderStatusMessageString(orderDetails);
  const subStatusMessage = isCheckedInAbandoned(orderDetails)
    ? checkPaymentTextForRefund(orderDetails)
    : getSubStatusMessageFromContentful(orderDetails);
  return (
    <div>
      {statusMessage !== '' && (
        <div
          className={`allSet ${orderStatus}-allSet ${service} ${selectedPickupOrderCollectionMethod} col-12 ${
            isPickupOrderCompleted(orderDetails) ? '' : 'p-0px-imp'
          }`}
        >
          <div className='col-12 statusMessage'>{statusMessage}</div>

          {/*  */}
          {getPickupOrderCheckedInMethodInfo(params)}
        </div>
      )}
      {subStatusMessage !== '' && !isCheckedInClosedOrder(orderDetails) && (
        <div className='substatusmsg' dangerouslySetInnerHTML={{ __html: subStatusMessage }}></div>
      )}
    </div>
  );
};
export const getPickupEmailConfirmationMessageJSX = params => {
  const { orderDetails } = params;
  const { orderSummarycontentData, enableClickCollectScheduleOrder, allowScheduleOrderForDispositions, service } =
    orderDetails;
  const enableClickCollectScheduleOrderFlag =
    enableClickCollectScheduleOrder && allowScheduleOrderForDispositions?.includes(service);
  return enableClickCollectScheduleOrderFlag
    ? orderSummarycontentData?.confirmationEmailSubHeadline !== '' && !isCheckedInClosedOrder(orderDetails) && (
        <div className='emailconfirmation-container'>
          <span className='emailconfirmation-msg'>{orderSummarycontentData?.confirmationEmailSubHeadline}</span>
        </div>
      )
    : null;
};
/*********************************************************************
 *
 *
 *********************************************************************/
const getPickupPartnerOTPPanelJSX = params => {
  const { orderDetails } = params;
  const { deliveryPartner, deliveryDetails } = orderDetails;
  const otp = deliveryPartner?.otp || deliveryDetails?.otp;
  return (
    !isPickupOrderCompleted(orderDetails) &&
    otp && (
      <>
        <div className='estimated-arrival estimated-arrival-refund driver-msg col-12'>
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.ASK_FOR_OTP)}
        </div>
        <div className='idAndTime time-delivery'>
          <div className='red otp'>{otp}</div>
        </div>
      </>
    )
  );
};

/*********************************************************************
 *
 *
 *********************************************************************/
export const displayPickupStatusPanelJSX = params => {
  return (
    <>
      {!isPickupOrderCompleted(params?.orderDetails) && getPickupOrderStoreNameOrderIdPanelJSX(params)}
      {isPickupOrderCheckedIn(params?.orderDetails) && getPickupOrderTimePanelJSX(params)}
      {getPickupStatusIconJSX(params)}
      {getPickupStatusMessageJSX(params)}
      {getPickupEmailConfirmationMessageJSX(params)}
      {getPickupPartnerOTPPanelJSX(params)}
    </>
  );
};

/*********************************************************************
 *
 *
 *********************************************************************/
const getPickupOrderCollectionMethodName = methodName => {
  switch (methodName) {
    case PICKUP_COLLECTION_METHOD?.DRIVE_THRU:
      return translateWithI18Next(TRANSLATE_MAPPING_KEY?.DRIVE_THRU);
    case PICKUP_COLLECTION_METHOD?.IN_STORE:
      return translateWithI18Next(TRANSLATE_MAPPING_KEY?.IN_STORE);
    // no default
  }
  return '';
};

/*********************************************************************
 *
 *
 *********************************************************************/
const getPickupOrderCollectionMethodMessage = methodName => {
  switch (methodName) {
    case PICKUP_COLLECTION_METHOD?.DRIVE_THRU:
      return translateWithI18Next(TRANSLATE_MAPPING_KEY?.PICKUP_FROM_WINDOW);
    case PICKUP_COLLECTION_METHOD?.IN_STORE:
      return translateWithI18Next(TRANSLATE_MAPPING_KEY?.PICKUP_INSIDE_RESTAURANT);
    // no default
  }
  return '';
};

/*********************************************************************
 * getPickupStoreClosedSoonInfoModalJSX - show when 30min left to close
 * params
 *********************************************************************/
export const getPickupStoreClosedInfoModalJSX = params => {
  const { updateSetOrderDetails, orderDetails } = params;
  return (
    <div className='closingsoon-wrapper'>
      <img src={IMAGE_PATH?.CartError} alt='closedOrderModal' />
      <div className='message-cancel'>
        <div className='selected-store-name'>{orderDetails?.store?.name}</div>
        <div>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.HAS_NOW_CLOSED)}</div>
      </div>
      <div className='message-refund closing-msg'>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDER_NO_LONGER_CHECKEDIN)}
      </div>
      <div className='buttonItems'>
        <ButtonComp
          data-testid='pickup-store-close-soon-btn'
          className='button blackButton blackBorder save-button'
          onClick={() => updateSetOrderDetails({ pickupClosedInfoModal: false })}
        >
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.OK)}
        </ButtonComp>
      </div>
    </div>
  );
};

/*********************************************************************
 * getPickupStoreClosingSoonInfoModalJSX - show when 45min left to close
 * params
 *********************************************************************/
export const getPickupStoreClosingSoonInfoModalJSX = params => {
  const { updateSetOrderDetails, orderDetails, lastCheckIn } = params;
  return (
    <div className='closingsoon-wrapper'>
      <img src={IMAGE_PATH?.Timeout} alt='closingsoonOrderModal' />
      <div className='message-cancel last-checkin'>
        <div className='selected-store-name'>{orderDetails?.store?.name}</div>
        <div>
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.LAST_ORDER_CHECKIN_AT)}
          <span className='selected-store-name'>{lastCheckIn}</span>
        </div>
      </div>
      <div className='message-refund closing-msg'>
        {orderDetails?.store?.name} {translateWithI18Next(TRANSLATE_MAPPING_KEY?.IS_CLOSING_SOON)}
      </div>
      <div className='message-refund ahead-time'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.CHECK_IN_AHEADTIME)}</div>
      <div className='buttonItems'>
        <ButtonComp
          data-testid='pickup-store-close-soon-btn'
          className='button blackButton blackBorder save-button'
          onClick={() => updateSetOrderDetails({ pickupClosingSoonModal: false })}
        >
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.OK)}
        </ButtonComp>
      </div>
    </div>
  );
};

/*********************************************************************
 * getPickupOrderCollectionMethodPanelJSX
 * params
 *********************************************************************/
export const getPickupOrderCollectionMethodPanelJSX = params => {
  const { orderDetails, updateSetOrderDetails } = params;
  const { pickupOrderAvailableCollectionPoints } = orderDetails;

  const isMethodAvailableForStore = name =>
    pickupOrderAvailableCollectionPoints?.findIndex(
      method => method.collectionPointName === PICKUP_COLLECTION_METHOD?.[name],
    ) !== -1;

  return (
    <div className='col-12 pickupOrder-collection-method-container'>
      <div className='col-12 pickupOrder-collection-method-header'>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.SELECT_PICK_UP_METHOD)}
      </div>
      <div className='col-12 pickupOrder-collection-method'>
        {Object?.keys(PICKUP_COLLECTION_METHOD)?.map(name => (
          <div
            className='col-12 pickupOrder-collection-method-node'
            data-testid={'PickupCollectionMethod_' + PICKUP_COLLECTION_METHOD?.[name]}
            onClick={() =>
              isMethodAvailableForStore(name) &&
              updateSetOrderDetails({
                selectedPickupOrderCollectionMethod: PICKUP_COLLECTION_METHOD?.[name],
                pickupClosedInfoModal: true,
              })
            }
          >
            <img src={IMAGE_PATH?.TakeAwayImg} className='methodIcon' alt={PICKUP_COLLECTION_METHOD?.[name]} />
            <div className='methodDetails'>
              <div className='methodTitle'>
                {getPickupOrderCollectionMethodName(PICKUP_COLLECTION_METHOD?.[name])}
                {!isMethodAvailableForStore(name) && (
                  <div className='methodTitle-Unavailable'>
                    {translateWithI18Next(TRANSLATE_MAPPING_KEY?.UNAVAILABLE)}
                  </div>
                )}
              </div>
              <span className='methodMessage'>
                {getPickupOrderCollectionMethodMessage(PICKUP_COLLECTION_METHOD?.[name])}
              </span>
            </div>
            {isMethodAvailableForStore(name) && (
              <img src={IMAGE_PATH?.Arrow} className='arrowIcon' alt={PICKUP_COLLECTION_METHOD?.[name]} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/*********************************************************************
 * getPickupOrderCancelPanelJSX
 * params
 *********************************************************************/
export const getPickupOrderCancelPanelJSX = params => {
  const { updateSetOrderDetails } = params;

  return (
    <div className='col-12 pickupOrderCancelPanel'>
      <span>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.CHANGED_YOUR_MIND)}</span>
      <span
        data-testid='pickupOrderCancelPanelBtn'
        tabIndex='0'
        className='pickupOrderCancelPanelBtn'
        onClick={() => updateSetOrderDetails({ isShowPickupOrderCancelModal: true })}
      >
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.CANCEL_ORDER_BUTTON_TEXT)}
      </span>
    </div>
  );
};

/*********************************************************************
 * getPickupCancelOrderModalJSX
 * params
 *********************************************************************/
export const getPickupCancelOrderModalJSX = params => {
  const { closeModalHandler, orderDetails } = params;
  const paymentType = orderDetails?.paymentReferences[0]?.paymentType;
  const storePhoneNumber = orderDetails?.store?.phoneNumber;
  return (
    <div className='pickupCancelOrderModal'>
      <img src={IMAGE_PATH?.CartError} alt='pickupCancelOrderModal' />
      <div className='message-cancel'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.CANCEL_PICKUP_ORDER)}</div>
      {paymentType !== PAYMENT_TENDER_TYPES?.PAY_BY_CASH && (
        <div className='message-refund'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.FULL_PAYMENT_REFUND)?.replace("{{storePhoneNumber}}", storePhoneNumber)}</div>
      )}
      <div className='buttonItems'>
        <ButtonComp
          data-testid='coupon-button-one'
          className='button whiteButton blackBorder closebtn-cls'
          onClick={closeModalHandler}
        >
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.KEEP_ORDER)}
        </ButtonComp>
        <ButtonComp
          data-testid='coupon-button-two'
          className='button blackButton blackBorder save-button'
          onClick={() => setEnablePickupCallSupportModal(params)}
        >
          {orderDetails.enableCancelOrderCallsupportModal
            ? translateWithI18Next(TRANSLATE_MAPPING_KEY?.CALL_SUPPORT_ORDER_BUTTON_TEXT)
            : translateWithI18Next(TRANSLATE_MAPPING_KEY?.CANCEL_ORDER_BUTTON_TEXT)}
        </ButtonComp>
      </div>
    </div>
  );
};

/*********************************************************************
 * getPickupGeoLocationVerifyModalJSX
 * params
 *********************************************************************/
export const getPickupGeoLocationVerifyModalJSX = params => {
  const { closeModalHandler } = params;
  return (
    <div className='pickupGeoLocationVerifyModal'>
      <img src={IMAGE_PATH?.LocationError} alt='pickupGeoLocationVerifyModal' />
      <div className='message-cancel'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.READY_TO_START_COOKING)}</div>
      <div className='message-refund'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.CONFIRM_YOU_ARE_HERE_TEXT)}</div>
      <div className='buttonItems'>
        <ButtonComp
          data-testid='coupon-button-one'
          className='button whiteButton blackBorder closebtn-cls'
          onClick={closeModalHandler}
        >
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.I_AM_ELSE_WHERE_TEXT)}
        </ButtonComp>
        <ButtonComp
          data-testid='coupon-button-two'
          className='button blackButton blackBorder save-button'
          onClick={() => triggerPickupCheckedIn(params)}
        >
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.I_AM_HERE_TEXT)}
        </ButtonComp>
      </div>
    </div>
  );
};
